import { Component, effect, HostListener, OnInit, signal } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { DatePickerComponent } from 'src/app/shared/ui-common/date-picker/date-picker.component';
import { WellbeingColorCodes, WellbeingColors } from 'src/app/shared/data/enums/wellbeing-colors.enum';
import { HighlightedDates } from 'src/app/shared/data/models/highilighted-dates.model';
import { environment } from 'src/environments/environment';
import { ButtonComponent } from 'src/app/shared/ui-common/button/button.component';
import { Chart, ChartConfiguration, ChartOptions, ScriptableContext } from 'chart.js';
import { Plugin } from 'chart.js';
import { ChartComponent } from 'src/app/shared/ui-common/chart/chart.component';
import { ToggleComponent, ToggleOption } from 'src/app/shared/ui-common/toggle/toggle.component';
import { PillComponent } from 'src/app/shared/ui-common/pill/pill.component';
import { FeaturePillMenuComponent } from 'src/app/shared/feature-pill-menu/feature-pill-menu.component';
import { MenuItem } from 'src/app/shared/data/models/menu-item.model';
import { PillService } from 'src/app/shared/data/pill.service';
import { Router, RouterLink, RouterModule } from '@angular/router';
import {
  IndividualScores,
  Metric,
  StatsService,
  TeamStats,
  WellbeingInterval,
  WellbeingStats
} from '../data/stats.service';
import { MarkdownPipe } from "../../../shared/data/pipes/markdown.pipe";
import { SharedService } from 'src/app/shared/data/shared.service';
import { TranslatePipe } from 'src/app/shared/data/pipes/translate.pipe';
import { TranslationService } from 'src/app/shared/data/translation.service';
import { isSameDate } from 'src/app/shared/util/shared.util';

interface Team {
  id: number;
  name: string;
}

interface LegendItem {
  label: string;
  color: string;
  selected: boolean;
}

@Component({
  selector: 'app-feature-stats',
  imports: [
    CommonModule,
    DatePickerComponent,
    ButtonComponent,
    ChartComponent,
    ToggleComponent,
    PillComponent,
    FeaturePillMenuComponent,
    MarkdownPipe,
    TranslatePipe,
    RouterModule,
    RouterLink
],
  templateUrl: './feature-stats.component.html',
  styleUrl: './feature-stats.component.scss',
})
export class FeatureStatsComponent implements OnInit {
  pillExpanded = false;
  isMobile = window.innerWidth < 500;
  lightModeTeam = '#B72B54';
  lightModeBlue = '#2DADC8';
  darkModeBlue = '#00C8FF';
  lightModeMood = '#FB6A18';
  darkModeMood = '#FF2C2C';
  lightModeRelaxed = '#58D289';
  lightModeEnergy = '#C53333';
  lightModeSleep = '#FFB800';
  lightModeMotivation = '#141761';
  s3BaseUrl = environment.s3BaseUrl;
  specificLegend: LegendItem[] = [
    {
      label: 'WebApp.v1.Survey.Mind.Question.Mood',
      color: this.lightModeMood,
      selected: true
    },
    {
      label: 'WebApp.v1.Survey.Mind.Question.Relaxation',
      color: this.lightModeRelaxed,
      selected: false
    },
    {
      label: 'WebApp.v1.Survey.Mind.Question.Energy',
      color: this.lightModeEnergy,
      selected: false
    },
    {
      label: 'WebApp.v1.Survey.Mind.Question.Sleep',
      color: this.lightModeSleep,
      selected: false
    },
    {
      label: 'WebApp.v1.Survey.Mind.Question.Motivation',
      color: this.lightModeMotivation,
      selected: false
    }
  ];
  scopeOptions: ToggleOption[] = [{
    label: OVERALL,
    value: 1
  },
  {
    label: SPECIFIC,
    value: 2
  }];
  userContextOptions: ToggleOption[] = [{
    label: YOU,
    value: 1
  },
  {
    label: TEAM,
    value: 2
  }];
  highlightedDates: HighlightedDates[] = [];
  selectedDate = new Date();
  selectedIndex = 0;
  selectedStats: WellbeingStats[] = [];
  configuration!: ChartConfiguration;
  chartInstance: Chart | null = null;
  selectedScope = this.scopeOptions[0];
  selectedUserContext = this.userContextOptions[0];
  teamsDropdownOpen = false;
  teams: Team[] = [];
  selectedDepartment: Team | null = null;
  stats: WellbeingStats[] = [];
  selectedMenuItem = signal<MenuItem | null>(null);
  nextAvailableMenuItem = signal<MenuItem | null>(null);
  selectedStat!: WellbeingStats | null;
  teamStats: TeamStats[] = [];
  selectedTeamMetrics: Metric[] = [];
  selectedTeamIndex = 0;
  hideOverallSpecificToggle = false;
  companyLogo = signal<string>('');
  companyName = '';

  private prevSelectedIndex = 0;
  private animationProgress = 1;
  private isAnimating = false;
  private animationStartTime = 0;
  private animationDuration = 300;
  private activeColor = this.primaryColor;
  private wellbeingIntervals: WellbeingInterval[] = [];

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < 500;
  };

  get dateMonth(): string {
    const languageCode = this.translateService.getMonthLanguageCode();
    const month = this.selectedDate.toLocaleString(languageCode, { month: 'long' });
    const day = this.selectedDate.toLocaleString('default', { day: 'numeric' });
    const year = this.selectedDate.toLocaleString('default', { year: 'numeric' });

    const currentYear = new Date().getFullYear();
    const selectedYear = this.selectedDate.getFullYear();

    if (selectedYear < currentYear) {
      return `${day} ${month} ${year}`;
    }

    return `${month} ${day}`;
  }

  get primaryColor(): string {
    return this.selectedUserContext.value === 1 ? this.lightModeBlue : this.lightModeTeam;
  }

  get wellbeingTitle(): string {
    if (this.selectedUserContext.label === YOU) {
      return 'WebApp.v1.Stats.YourWellbeingHistory';
    } else if (this.selectedUserContext.label === TEAM) {
      return this.selectedDepartment ? 'WebApp.v1.Stats.TeamWellbeingHistory' : 'WebApp.v1.Stats.CompanyWellbeingHistory';
    }

    return '';
  }

  get isHowdyLogo(): boolean {
    return this.companyLogo().includes('HRONWellbeing');
  }

  get buttonText(): string {
    return this.nextAvailableMenuItem()?.title ? 'WebApp.v1.Shared.Buttons.Next' : 'WebApp.v1.Shared.Buttons.Menu';
  }

  get buttonIcon(): string {
    if (this.selectedMenuItem()?.icon && this.selectedMenuItem()?.id !== -1) {
      return this.selectedMenuItem()?.icon ?? '';
    }

    return this.nextAvailableMenuItem()?.icon ?? '';
  }

  get hasData(): boolean {
    return this.configuration && this.configuration.data.datasets[0].data.length > 0;
  }

  constructor(
    private pillService: PillService,
    private router: Router,
    private statsService: StatsService,
    private sharedService: SharedService,
    private translateService: TranslationService,
    private location: Location
  ) {
    effect(() => {
      this.selectedMenuItem.set(pillService.getCurrentMenuItem());
      this.nextAvailableMenuItem.set(pillService.getNextAvailableMenuItem());
    });
  }

  ngOnInit(): void {
    this.companyLogo.set(this.sharedService.getCompanyLogoUrl(true));
    this.companyName = this.sharedService.getCompanyName();
    this.statsService.stats$.subscribe(stats => {
      if (stats) {
        this.wellbeingIntervals = stats.wellbeingIntervals;
        this.stats = stats.wellbeingStats;
        this.selectedStat = this.stats[0];

        this.setupHighlightedDates();
        this.setupChartData();
        this.drawChart();
      }
    });

    this.statsService.teamStats$.subscribe(teamStats => {
      if (teamStats) {
        this.teamStats = teamStats;
        this.selectedTeamIndex = 0;
        this.selectTeamMetrics();
        this.teams = this.teamStats.sort((a, b) => b.participationThisMonth - a.participationThisMonth).map(x => ({
          id: x.metrics[0]?.departmentId,
          name: x.name
        }));
      }
    });
  }

  goBack(): void {
    this.location.back();
  }

  getHowdyLogo(): void {
    this.companyLogo.set(this.sharedService.getHowdyLogoUrl(true));
  }

  selectTeamMetrics(): void {
    this.selectedTeamMetrics = this.teamStats[this.selectedTeamIndex]?.metrics
      .filter(x => x.avgScorePerAnswer !== null)
      .slice(0, 6)
      .reverse() ?? [];

    if (this.selectedTeamMetrics.length === 0) {
      return;
    }

    this.selectedTeamIndex = this.teamStats.findIndex(x =>
      x.metrics[0]?.departmentId === this.selectedTeamMetrics[0]?.departmentId);

    if (this.selectedTeamMetrics?.length > 0) {
      this.selectedDepartment = this.teams.find(x => x.id === this.selectedTeamMetrics[0]?.departmentId) ?? null;
    }
  }

  setupHighlightedDates(): void {
    if (this.selectedUserContext.label === YOU) {
      this.highlightedDates = this.stats.map(stat => {
        return {
          date: stat.utcDate,
          color: this.getWellbeingColor(stat.totalScore)[0],
          hasNote: !!stat.note
        };
      });
    } else {
      this.highlightedDates = this.teamStats[this.selectedTeamIndex]?.metrics
        .filter(x => x.avgScorePerAnswer !== null)
        .map(stat => {
          return {
            date: new Date(stat.monthStart),
            color: this.getWellbeingColor(stat.avgScorePerAnswer ?? 0)[0],
            hasNote: false
          };
      });
    }

    if (!this.highlightedDates || this.highlightedDates?.length === 0) {
      return;
    }

    this.selectedDate = this.highlightedDates[0]?.date ?? new Date();
  }

  setupChartData(direction = RIGHTLEFT): void {
    const selectedIndex = this.stats?.findIndex((stat) => {
      return isSameDate(stat.utcDate, this.selectedDate);
    });

    if (selectedIndex === -1) {
      return;
    }

    const [firstIndex, lastIndex] = this.getFirstLastIndex(selectedIndex, direction, this.stats.length);

    this.selectedStats = this.stats.slice(firstIndex, lastIndex).reverse();
  }

  setupTeamChartData(direction = RIGHTLEFT): void {
    const metricsWithScore = this.teamStats[this.selectedTeamIndex]?.metrics?.filter(x => x.avgScorePerAnswer !== null) ?? [];
    const selectedIndex = metricsWithScore?.findIndex(stat => isSameDate(new Date(stat.monthStart), this.selectedDate));

    if (selectedIndex === -1) {
      return;
    }

    const [firstIndex, lastIndex] = this.getFirstLastIndex(selectedIndex, direction, metricsWithScore.length);

    this.selectedTeamMetrics = metricsWithScore.slice(firstIndex, lastIndex).reverse();
  }

  drawChart(): void {
    const lineChartData = {
      labels: this.selectedStats.map((stat) => stat.utcDate.toLocaleString('default', { month: 'short', day: 'numeric' })),
      datasets: [
        {
          data: this.selectedStats.map((stat) => stat.totalScore),
          borderColor: this.primaryColor,
          pointBorderColor: this.primaryColor,
          backgroundColor: (ctx: ScriptableContext<'line'>): CanvasGradient | null => this.getGradient(ctx, this.primaryColor),
          pointBackgroundColor: (ctx: ScriptableContext<'line'>): string | undefined => this.getPointBackgroundColor(
            ctx,
            'totalScore',
            this.primaryColor
          ),
          borderWidth: 3,
          pointHoverBorderWidth: 3,
          pointRadius: 6,
          pointHoverRadius: 6,
          fill: true,
          clip: false
        }
      ]
    } as ChartConfiguration<'line'>['data'];

    this.configuration = {
      type: 'line',
      data: lineChartData,
      options: this.getChartOptions(),
      plugins: [this.updateSelectedSegment()]
    };
  }

  drawTeamChart(): void {
    if (!this.selectedTeamMetrics || this.selectedTeamMetrics?.length === 0) {
      this.selectedTeamMetrics = [];
    }

    const lineChartData = {
      labels: this.selectedTeamMetrics.map(x => new Date(x.monthStart).toLocaleString('default', { month: 'short', day: 'numeric' })),
      datasets: [
        {
          data: this.selectedTeamMetrics.map(x => x.avgScorePerAnswer),
          borderColor: this.primaryColor,
          pointBorderColor: this.primaryColor,
          backgroundColor: (ctx: ScriptableContext<'line'>): CanvasGradient | null => this.getGradient(ctx, this.primaryColor),
          pointBackgroundColor: (ctx: ScriptableContext<'line'>): string | undefined => this.getPointBackgroundColor(
            ctx,
            'avgScorePerAnswer',
            this.primaryColor
          ),
          borderWidth: 3,
          pointHoverBorderWidth: 3,
          pointRadius: 6,
          pointHoverRadius: 6,
          fill: true,
          clip: false,
        }
      ]
    } as ChartConfiguration['data'];

    this.configuration = {
      type: 'line',
      data: lineChartData,
      options: this.getChartOptions(),
      plugins: [this.updateSelectedSegment()]
    };
  }

  drawSpecificTypeChart(newDataset: string | null = null): void {
    const selectedLegends = this.specificLegend.filter((legend) => legend.selected).map((legend) => {
      const label = legend.label.split('.').pop()?.toLowerCase() ?? '';

      return {
        ...legend,
        scoreKey: `${label}Score` as keyof IndividualScores
      };
    });

    const backgroundDatasets = selectedLegends.map((legend) => {
      return {
        data: this.selectedStats.map((stat) => stat.individualScores[legend.scoreKey] / 4),
        borderColor: 'transparent',
        pointBorderColor: 'transparent',
        pointBackgroundColor: 'transparent',
        backgroundColor: selectedLegends.length > 1
          ? 'transparent'
          : (ctx: ScriptableContext<'line'>): CanvasGradient | null => this.getGradient(ctx, legend.color),
        borderWidth: 0,
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: selectedLegends.length === 1,
        order: 2,
        animation: newDataset !== null && newDataset === legend.label,
      };
    }) as ChartConfiguration['data']['datasets'];

    const lineDatasets = selectedLegends.map((legend) => {
      return {
        label: legend.label,
        data: this.selectedStats.map((stat) => stat.individualScores[legend.scoreKey] / 4),
        borderColor: legend.color,
        pointBorderColor: legend.color,
        backgroundColor: 'transparent',
        pointBackgroundColor: legend.color,
        pointHoverBackgroundColor: legend.color,
        borderWidth: 3,
        pointHoverBorderWidth: 3,
        pointRadius: 6,
        pointHoverRadius: 6,
        fill: false,
        clip: false,
        order: 1,
        animation: newDataset !== null && newDataset === legend.label
      };
    }) as ChartConfiguration['data']['datasets'];

    this.configuration = {
      type: 'line',
      data: {
        labels: this.selectedStats.map((stat) => stat.utcDate.toLocaleString('default', { month: 'short', day: 'numeric' })),
        datasets: [...backgroundDatasets, ...lineDatasets]
      },
      options: this.getChartOptions(),
      plugins: [
        this.updateSelectedSegment(),
        this.spreadOverlappingSpecificPoints()
      ]
    };
  }

  getChartOptions(): ChartOptions {
    const specificChartTicks = {
      padding: 20,
      color: '#898989',
      font: {
        family: 'Poppins',
        weight: 500,
        size: 12
      },
      callback: (value: number): string[] => this.getTranslatedYAxisLabels()[Number(value)] || []
    } as ChartOptions['scales'];

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false }
      },
      scales: {
        x: {
          position: 'top',
          border: { display: false },
          grid: {
            display: true,
            color: 'rgba(0, 0, 0, 0.1)'
          },
          ticks: {
            padding: 20,
            color: this.primaryColor,
            font: {
              family: 'Poppins',
              weight: 700
            }
          }
        },
        y: {
          beginAtZero: true,
          min: 0,
          max: this.selectedScope.label === OVERALL ? 100 : 5,
          grid: { display: false },
          border: { display: false },
          ticks: this.selectedScope.label === OVERALL ? { color: '#fff' } : specificChartTicks
        }
      },
    };
  }

  onMenuItemChange(menuItem: MenuItem): void {
    this.nextAvailableMenuItem.set(this.pillService.getNextAvailableMenuItem());
    this.selectedMenuItem.set(menuItem);
  }

  goToNextSurvey(): void {
    const menuItem = this.selectedMenuItem();

    if (menuItem && menuItem.isAvailable) {
      this.pillService.setSelectedMenuItemById(menuItem.id);
      this.router.navigate(['/survey']);
    } else {
      this.router.navigate(['/available-surveys']);
    }
  }

  selectedDateChanged(date: Date): void {
    if (this.selectedUserContext.label === TEAM) {
      const index = this.selectedTeamMetrics.findIndex(stat => isSameDate(new Date(stat.monthStart), date));
      this.selectedDate = date;

      if (index < 0) {
        this.setupTeamChartData();
      }

      const dateIndex = index < 0
        ? 0
        : Math.abs(index - this.selectedTeamMetrics.length + 1);
      this.startSelectedSegmentTransition(dateIndex);

      if (index < 0) {
        this.drawTeamChart();
      }
    } else {
      const index = this.selectedStats.findIndex(stat =>isSameDate(stat.utcDate, date));
      this.selectedDate = date;

      if (index < 0) {
        this.setupChartData();
      }

      const dateIndex = index < 0
        ? 0
        : Math.abs(index - this.selectedStats.length + 1);

      this.selectedStat = this.stats[dateIndex];
      this.startSelectedSegmentTransition(dateIndex);

      if (index < 0) {
        if (this.selectedScope.label === OVERALL) {
          this.drawChart();
        } else {
          this.drawSpecificTypeChart();
        }
      }
    }
  }

  onOverallSpecificChange(option: ToggleOption): void {
    this.selectedScope = option;

    if (this.selectedScope.label === OVERALL) {
      this.activeColor = this.primaryColor;
      this.drawChart();
    } else {
      this.activeColor = this.lightModeMood;
      this.drawSpecificTypeChart();
    }
  }

  onYouTeamChange(option: ToggleOption): void {
    this.selectedUserContext = option;
    this.hideOverallSpecificToggle = this.selectedUserContext.label === TEAM;
    this.selectedIndex = 0;
    this.prevSelectedIndex = 0;

    if (this.selectedUserContext.label === TEAM) {
      this.selectTeamMetrics();
      this.setupHighlightedDates();
      this.selectedDate = this.selectedTeamMetrics.length === 0
        ? new Date()
        : new Date(this.selectedTeamMetrics[this.selectedTeamMetrics.length - 1].monthStart);

      this.activeColor = this.primaryColor;
      this.selectedScope = this.scopeOptions[0];
      this.setupTeamChartData();
      this.drawTeamChart();

      return;
    }

    if (this.stats.length === 0) {
      return;
    }

    this.setupHighlightedDates();
    this.selectedDate = this.highlightedDates[0].date;
    this.setupChartData();

    if (this.selectedScope.label === OVERALL) {
      this.activeColor = this.primaryColor;
      this.drawChart();
    } else {
      this.activeColor = this.lightModeMood;
      this.drawSpecificTypeChart();
    }
  }

  onLegendSelected(legendItem: LegendItem): void {
    const numberOfSelected = this.specificLegend.filter((legend) => legend.selected).length;

    if (numberOfSelected === 1 && legendItem.selected) {
      return;
    }

    legendItem.selected = !legendItem.selected;

    if (legendItem.selected) {
      this.activeColor = legendItem.color;
    } else {
      this.activeColor = this.specificLegend.find((legend) => legend.selected)?.color ?? this.lightModeMood;
    }

    this.drawSpecificTypeChart(legendItem.label);
  }

  onTeamSelected(team: Team): void {
    this.selectedTeamIndex = this.teams.findIndex(x => x.name === team.name);
    this.selectedDepartment = team;
    this.selectTeamMetrics();
    this.teamsDropdownOpen = false;
    this.setupHighlightedDates();

    if (this.selectedScope.label === OVERALL) {
      this.activeColor = this.primaryColor;
      this.selectedIndex = 0;
      this.drawTeamChart();
    } else {
      this.activeColor = this.lightModeMood;
      this.drawSpecificTypeChart();
    }
  }

  private startSelectedSegmentTransition(targetIndex: number): void {
    if (this.selectedIndex === targetIndex) {
      return;
    }

    let lastIndex = this.selectedUserContext.label === TEAM
      ? this.selectedTeamMetrics.length - 1
      : this.selectedStats.length - 1;

    if (targetIndex === lastIndex) {
      targetIndex = 0;
      const direction = this.selectedIndex > targetIndex ? RIGHTLEFT : LEFTRIGHT;

      this.selectedIndex = 0;
      this.prevSelectedIndex = 1;

      if (this.selectedUserContext.label === TEAM) {
        const teamStats = this.teamStats[this.selectedTeamIndex]?.metrics.filter(x => x.avgScorePerAnswer !== null);
        const lastStat = teamStats[teamStats.length - 1];
        const isLastStat = isSameDate(new Date(lastStat.monthStart), new Date(this.selectedTeamMetrics[targetIndex].monthStart));

        if (isLastStat && direction === RIGHTLEFT) {
          this.selectedIndex = lastIndex;
          this.prevSelectedIndex = lastIndex - 1;
          this.selectedDate = new Date(lastStat.monthStart);
        } else {
          this.setupTeamChartData(direction);

          if (direction === LEFTRIGHT && this.selectedIndex === 0) {
            this.selectedIndex = this.selectedTeamMetrics.length - 2;
            this.prevSelectedIndex = this.selectedTeamMetrics.length - 2;
          }

          this.drawTeamChart();
        }
      } else {
        const lastStat = this.stats[this.stats.length - 1];
        const isLastStat = isSameDate(lastStat.utcDate, this.selectedStats[targetIndex].utcDate);

        if (isLastStat) {
          this.selectedIndex = lastIndex;
          this.prevSelectedIndex = lastIndex - 1;
          this.selectedDate = new Date(lastStat.utcDate);
        } else {
          this.setupChartData();

          if (this.selectedScope.label === OVERALL) {
            this.drawChart();
          } else {
            this.drawSpecificTypeChart();
          }
        }
      }
    } else if (targetIndex === lastIndex + 1) {
      lastIndex = this.isMobile ? 3 : Math.min(5, this.stats.length - 1);
      targetIndex = lastIndex;
      this.selectedIndex = lastIndex - 1;
      this.prevSelectedIndex = lastIndex - 2;

      if (this.selectedUserContext.label === TEAM) {
        this.setupTeamChartData(LEFTRIGHT);
        this.drawTeamChart();
      } else {
        this.setupChartData(LEFTRIGHT);

        if (this.selectedScope.label === OVERALL) {
          this.drawChart();
        } else {
          this.drawSpecificTypeChart();
        }

        this.selectedDate = this.selectedStats[1].utcDate;
      }
    } else {
      this.prevSelectedIndex = this.selectedIndex;
      this.selectedIndex = targetIndex;
    }

    this.animationProgress = 0;
    this.isAnimating = true;
    this.animationStartTime = performance.now();

    if (this.chartInstance) {
      this.animateTransition();
    }
  }

  private animateTransition(): void {
    const currentTime = performance.now();
    const elapsed = currentTime - this.animationStartTime;
    this.animationProgress = Math.min(elapsed / this.animationDuration, 1);

    if (this.animationProgress < 1) {
      this.chartInstance?.update();
      requestAnimationFrame(() => this.animateTransition());
    } else {
      this.isAnimating = false;
      this.chartInstance?.update();
    }
  }

  private updateSelectedSegment(): Plugin<'line'> {
    const numberOfIndexes = this.selectedUserContext.label === TEAM
      ? this.selectedTeamMetrics.length - 1
      : this.selectedStats.length - 1;

    if (numberOfIndexes < 2) {
      return {
        id: 'updateSelectedSegment',
        beforeDraw: (chart: Chart): void => {
          this.chartInstance = chart;
        }
      };
    }

    return {
      id: 'updateSelectedSegment',
      beforeDraw: (chart: Chart): void => {
        this.chartInstance = chart;
        let currentIndex = chart.data.datasets[0].data.length - 1 - this.selectedIndex;
        let prevIndex = chart.data.datasets[0].data.length - 1 - this.prevSelectedIndex;

        if (currentIndex === 0) {
          currentIndex = 1;
          prevIndex = 0;
        }

        if (currentIndex < 0) {
          return;
        }

        if (prevIndex <= 0) {
          this.isAnimating = false;
        }

        const rightToLeftDirection = prevIndex > currentIndex ? 1 : -1;
        let startIndex, endIndex;

        if (this.isAnimating) {
          if (rightToLeftDirection === 1) {
            startIndex = prevIndex - this.animationProgress * (prevIndex - currentIndex);
            endIndex = startIndex - 1;
          } else {
            startIndex = prevIndex + this.animationProgress * (currentIndex - prevIndex);
            endIndex = startIndex - 1;
          }
        } else {
          startIndex = currentIndex;
          endIndex = currentIndex - 1;
          this.drawDashedLine(chart);
        }

        this.drawGradientRect(chart, startIndex, endIndex);
      }
    };
  }

  private drawGradientRect(chart: Chart, startIndex: number, endIndex: number): void {
    const { ctx, chartArea } = chart;
    const startX = chart.scales['x'].getPixelForValue(startIndex);
    const endX = chart.scales['x'].getPixelForValue(endIndex);
    const segmentWidth = endX - startX;

    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    gradient.addColorStop(0, '#FFFFFF');
    gradient.addColorStop(1, `${this.activeColor}4D`);

    ctx.save();
    ctx.fillStyle = gradient;
    ctx.fillRect(startX, chartArea.top, segmentWidth, chartArea.bottom - chartArea.top);
    ctx.restore();
  }

  private drawDashedLine(chart: Chart): void {
    const { ctx, chartArea } = chart;
    const index = chart.data.datasets[0].data.length - 1 - this.selectedIndex;

    if (index <= 0) {
      return;
    }

    const startX = chart.scales['x'].getPixelForValue(index - 1);
    const endX = chart.scales['x'].getPixelForValue(index);
    const segmentWidth = endX - startX;

    const dashStartX = startX + 25;
    const dashEndX = startX + segmentWidth - 25;
    const availableWidth = dashEndX - dashStartX;
    const dashLength = 2;
    const dashGap = 2;
    const totalDashUnit = dashLength + dashGap;
    const numberOfDashes = Math.floor(availableWidth / totalDashUnit);
    const dashY = chartArea.top - 20 - 16;

    ctx.save();
    ctx.strokeStyle = this.primaryColor;
    ctx.lineWidth = 1;

    for (let i = 0; i < numberOfDashes; i++) {
      const dashX = dashStartX + i * totalDashUnit;
      ctx.beginPath();
      ctx.moveTo(dashX, dashY);
      ctx.lineTo(dashX + dashLength, dashY);
      ctx.stroke();
    }

    ctx.restore();
  }

  private spreadOverlappingSpecificPoints(): Plugin<'line'> {
    return {
      id: 'spreadOverlappingSpecificPoints',
      afterDatasetsUpdate: (chart: Chart<'line'>): void => {
        const groups = new Map<string, Array<{ datasetIndex: number; dataIndex: number }>>();

        chart.data.datasets.forEach((dataset, datasetIndex) => {
          if (!dataset.label) {
            return;
          }

          dataset.data.forEach((value, dataIndex) => {
            const key = `${dataIndex}:${value}`;
            const points = groups.get(key) ?? [];
            points.push({ datasetIndex, dataIndex });
            groups.set(key, points);
          });
        });

        groups.forEach((points) => {
          if (points.length < 2) {
            return;
          }

          const offsetStep = this.isMobile ? 8 : 10;
          const startOffset = -((points.length - 1) * offsetStep) / 2;

          points.forEach((point, index) => {
            const element = chart.getDatasetMeta(point.datasetIndex).data[point.dataIndex];

            if (!element) {
              return;
            }

            element.x += startOffset + (index * offsetStep);
          });
        });
      }
    };
  }

  private getGradient(ctx: ScriptableContext<'line'>, color: string): CanvasGradient | null {
    const chart = ctx.chart;
    const chartArea = chart.chartArea;

    if (!chartArea) {
      return null;
    }

    const { ctx: canvasCtx } = chart;
    const gradient = canvasCtx.createLinearGradient(
      chartArea.left,
      chartArea.top,
      chartArea.left,
      chartArea.bottom
    );

    if (this.selectedScope.value === 2) {
      gradient.addColorStop(0.3, `${color}80`);
      gradient.addColorStop(0.9, 'rgba(255, 255, 255, 0.00)');
    } else {
      gradient.addColorStop(0, color);
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0.00)');
    }

    // TODO: dark mode blue gradient
    // gradient.addColorStop(0, 'rgba(56, 196, 226, 0.31');
    // gradient.addColorStop(0.8747, 'rgba(43, 66, 45, 0.00)');

    return gradient;
  }

  private getPointBackgroundColor(
    ctx: ScriptableContext<'line'>,
    property: keyof WellbeingStats | keyof Metric,
    defaultColor: string
  ): string | undefined {
    const chart = ctx.chart;
    const { ctx: chartArea } = chart;

    if (!chartArea) {
      return;
    }

    const numberOfIndexes = this.selectedUserContext.label === TEAM
      ? this.selectedTeamMetrics?.length - 1
      : this.selectedStats?.length - 1;
    const index = ctx.dataIndex;

    if (numberOfIndexes < index) {
      return defaultColor;
    }

    let value = -1;

    if (this.selectedUserContext.label === TEAM) {
      value = this.selectedTeamMetrics[index][property as keyof Metric] as number;
    } else {
      value = this.selectedStats[index][property as keyof WellbeingStats] as number;
    }

    if (value === null || numberOfIndexes - this.selectedIndex !== index) {
      return defaultColor;
    }

    if (value < 10) {
      return this.getChartSpecificColor(value);
    }

    return this.getWellbeingColor(value)[1];
  }

  private getChartSpecificColor(value: number): string {
    if (value === 4) {
      return WellbeingColorCodes.DarkGreen;
    } else if (value >= 3) {
      return WellbeingColorCodes.Green;
    } else if (value >= 2) {
      return WellbeingColorCodes.LightGreen;
    } else if (value >= 1) {
      return WellbeingColorCodes.Yellow;
    }

    return WellbeingColorCodes.Red;
  }

  private getWellbeingColor(value: number): [WellbeingColors, string] {
    let intervalIndex = 4;
    const darkGreenInterval = this.wellbeingIntervals[intervalIndex--];
    const greenInterval = this.wellbeingIntervals[intervalIndex--];
    const lightGreenInterval = this.wellbeingIntervals[intervalIndex--];
    const yellowInterval = this.wellbeingIntervals[intervalIndex--];

    if (darkGreenInterval && value >= darkGreenInterval.start) {
      return [WellbeingColors.DarkGreen, WellbeingColorCodes.DarkGreen];
    } else if (greenInterval && value >= greenInterval.start) {
      return [WellbeingColors.Green, WellbeingColorCodes.Green];
    } else if (lightGreenInterval && value >= lightGreenInterval.start) {
      return [WellbeingColors.LightGreen, WellbeingColorCodes.LightGreen];
    } else if (yellowInterval && value >= yellowInterval.start) {
      return [WellbeingColors.Yellow, WellbeingColorCodes.Yellow];
    }

    return [WellbeingColors.Red, WellbeingColorCodes.Red];
  }

  private getFirstLastIndex(selectedIndex: number, direction: number, length: number): [number, number] {
    const numberOfPeriods = this.isMobile ? 3 : Math.min(6, length);

    if (direction === RIGHTLEFT) {
      const startIndex = selectedIndex;
      const endIndex = Math.min(selectedIndex + numberOfPeriods, length);

      return [startIndex, endIndex];
    }
    let startIndex = (selectedIndex + 1 - numberOfPeriods) + 1;
    let endIndex = selectedIndex + 2;

    if (startIndex < 0) {
      startIndex = 0;
      endIndex = numberOfPeriods;
      this.selectedIndex = 0;
    }

    return [startIndex, endIndex];
  }

  private getTranslatedYAxisLabels(): string[][] {
    const labels: string[] = [
      'WebApp.v1.Survey.Mind.Answer.Option.AtNoTime',
      'WebApp.v1.Survey.Mind.Answer.Option.SomeOfTheTime',
      'WebApp.v1.Survey.Mind.Answer.Option.LessThanHalfOfTheTime',
      'WebApp.v1.Survey.Mind.Answer.Option.MoreThanHalfOfTheTime',
      'WebApp.v1.Survey.Mind.Answer.Option.MostOfTheTime',
      'WebApp.v1.Survey.Mind.Answer.Option.AtAllTime'
    ];

    return labels.map(label => {
      const translatedLabel = this.translateService.translate(label);

      if (translatedLabel.length > 20) {
        const splitIndex = translatedLabel.lastIndexOf(' ', 20);

        return [translatedLabel.slice(0, splitIndex), translatedLabel.slice(splitIndex)];
      }

      return [translatedLabel];
    });
  }
}

const OVERALL = 'WebApp.v1.Stats.Overall';
const SPECIFIC = 'WebApp.v1.Stats.Specific';
const YOU = 'WebApp.v1.Stats.You';
const TEAM = 'WebApp.v1.Stats.Team';
const RIGHTLEFT = 1;
const LEFTRIGHT = -1;
